import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BRANDING_IMAGES_SETTINGS } from '@constants/administration/organization/branding/branding-images-settings.constant';
import { DESIGN_PREVIEW_STATE } from '@constants/modal-options/modal-options';
import { Branding } from '@models/adminstration/organization/branding/branding';
import {
    BrandingImageDimension,
    BrandingImageSettings
} from '@models/adminstration/organization/branding/branding-image-settings';
import { TranslateService } from '@ngx-translate/core';
import { BrandingService } from '@services/administration/organization/configuration/branding/branding.service';
import { NotificationService } from '@services/notification/notification.service';
import { StorageService } from '@services/storage/storage.service';
import { cloneDeep } from 'lodash';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'scitis-admin-organization-branding',
    templateUrl: './branding.component.html',
    styleUrls: ['./branding.component.scss'],
    standalone: false
})
export class BrandingComponent implements OnChanges {
  private COMPONENT_NAME = 'BrandingComponent';
  imageSetting = BRANDING_IMAGES_SETTINGS;

  @Input()
  branding: Branding = new Branding();
  @Input()
  parentBranding: Branding = new Branding();

  @Input()
  isAdminRole: boolean = false;
  @Input()
  isSuperAdminRole: boolean = false;
  @Input()
  organizationId: number = -1;
  @Input()
  organizationName: string = '';
  @Input()
  brandingIsLoading: boolean = true;


  imageBackgroundColor: string = '';
  private environment: string = 'scitis';

  logoSettings: BrandingImageSettings;
  logoMobileSettings: BrandingImageSettings;
  favIconSettings: BrandingImageSettings;

  primaryColorToggle: boolean = false;
  secondaryColorToggle: boolean = false;
  highlightColorToggle: boolean = false;
  successColorToggle: boolean = false;
  warningColorToggle: boolean = false;
  infoColorToggle: boolean = false;
  dangerColorToggle: boolean = false;

  constructor(private brandingService: BrandingService,
              private storageService: StorageService,
              private notification: NotificationService,
              private translate: TranslateService) {

    const logo = this.imageSetting.LOGO;
    this.logoSettings = new BrandingImageSettings(logo.name, logo.label, logo.url, logo.type, logo.mimeTypes,
        logo.size.w, logo.size.h);
    const logoMobile = this.imageSetting.LOGO_MOBILE;
    this.logoMobileSettings = new BrandingImageSettings(logoMobile.name, logoMobile.label, logoMobile.url,
        logoMobile.type, logoMobile.mimeTypes, logoMobile.size.w, logoMobile.size.h);

    const favIcon = this.imageSetting.FAV_ICON;
    this.favIconSettings = new BrandingImageSettings(favIcon.name, favIcon.label, favIcon.url, favIcon.type,
        favIcon.mimeTypes, favIcon.size.w, favIcon.size.h);

  }

  onResetBrandingDetails() {
    const defaultOrParentBranding = cloneDeep(this.parentBranding);
    const branding = new Branding();
    branding.id = this.branding.id;
    branding.colors = defaultOrParentBranding.colors;

    const logoResetObservable = this.brandingService.verifyAndSetImageRequiredDimensions(
        defaultOrParentBranding.images.logo,
        this.imageSetting.LOGO.name,
        this.imageSetting.LOGO.label,
        new BrandingImageDimension(this.imageSetting.LOGO.size.w, this.imageSetting.LOGO.size.h)
    );
    logoResetObservable.subscribe(function (logo) {
      if (logo) {
        branding.images.logo = logo;
      }
    });

    const logoMobileResetObservable = this.brandingService.verifyAndSetImageRequiredDimensions(
        defaultOrParentBranding.images.logoMobile,
        this.imageSetting.LOGO_MOBILE.name,
        this.imageSetting.LOGO_MOBILE.label,
        new BrandingImageDimension(this.imageSetting.LOGO_MOBILE.size.w, this.imageSetting.LOGO_MOBILE.size.h)
    );
    logoMobileResetObservable.subscribe(function (logoMobile) {
      if (logoMobile) {
        branding.images.logoMobile = logoMobile;
      }
    });

    const faviconResetObservable = this.brandingService.verifyAndSetImageRequiredDimensions(
        defaultOrParentBranding.images.favIcon,
        this.imageSetting.FAV_ICON.name,
        this.imageSetting.FAV_ICON.label,
        new BrandingImageDimension(this.imageSetting.FAV_ICON.size.w, this.imageSetting.FAV_ICON.size.h)
    );
    faviconResetObservable.subscribe(function (favIcon) {
      if (favIcon) {
        branding.images.favIcon = favIcon;
      }
    });

    forkJoin([logoResetObservable, logoMobileResetObservable, faviconResetObservable]).subscribe(() => {
      this.branding = branding;
      this.brandingService.organizationBranding = branding;
      this.setImageBackgroundColor(this.branding);
      this.previewChange();
    }, (error) => {
      this.notification.error(this.translate.instant('There was an error resetting organization branding details'),
          error, this.COMPONENT_NAME, true);
    });

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.branding || changes.parentBranding || changes.brandingIsLoading) {
      if (this.brandingIsLoading || !this.branding) {
        return;
      }
      this.branding = this.getBrandingDetails(this.branding, this.parentBranding);
      this.parentBranding = this.getBrandingDetails(this.parentBranding, null);
      this.setImageBackgroundColor(this.branding);
    }
  }

  private setImageBackgroundColor(branding) {
    if (this.environment === 'looxr') {
      this.imageBackgroundColor = '#FFFFFF';
    } else if (this.environment === 'lorenz') {
      this.imageBackgroundColor = branding.colors.highlight;
    } else {
      this.imageBackgroundColor = branding.colors.primary;
    }
  }

  onCloseColorPicker(control: string, $event: string) {
    this.branding.colors[control] = $event;
    this.previewChange();
    this.setImageBackgroundColor(this.branding);
  }

  private getBrandingDetails(organizationBranding: Branding, parentOrganizationBranding: Branding | null): Branding {
    return this.brandingService.getOrganizationBrandingOrFallback(organizationBranding, parentOrganizationBranding);
  }

  onImageChanged($event: string, type: string) {
    switch (type) {
      case this.imageSetting.LOGO.name: {
        this.branding.images.logo = $event;
        break;
      }
      case this.imageSetting.LOGO_MOBILE.name: {
        this.branding.images.logoMobile = $event;
        break;
      }
      case this.imageSetting.FAV_ICON.name: {
        this.branding.images.favIcon = $event;
        break;
      }
    }
    this.previewChange();
  }

  applySecondaryColor(event: Event) {
    const {checked} = event.target as HTMLInputElement;
    this.branding.secondaryColorEnabled = checked;
    this.previewChange();
  }

  previewChange() {
    this.storageService.readValueFromIndexedDb(DESIGN_PREVIEW_STATE).then(res => {
      if(!res) {
        this.notification.designPreviewSubject();
      } else {
        const savedBranding = JSON.parse(res as string);
        if (this.brandingService.currentUserBranding.id === savedBranding.id) {
          this.brandingService.discardDesignPreview();
          this.notification.designPreviewSubject();
        }
      }
    });
    this.brandingService.changeDefaultOrganizationStyle(this.branding, true);
    this.storageService.writeValueToIndexedDb(DESIGN_PREVIEW_STATE, this.branding);
    this.brandingService.previewOrganizationName = this.organizationName;
  }
}
